import React from "react";
import { Check, Plus, X } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  FieldError, FormField, IconButton, Input, cn,
} from "../../ui";
import { OptionImageControl } from "../QuizImageField";
import {
  addAnswer, addOption, answerKind, letterOf, removeAnswer, removeOption, setAnswer, setOption,
} from "./questionRules";

/** One answer option: letter, correct toggle, text, remove — and, under the row,
 *  an Image choice option's picture. */
function OptionRow({ letter, option, fixed, onToggleCorrect, onTextChange, onRemove, children }) {
  return (
    <div
      className={cn(
        "tw-flex tw-flex-col tw-gap-1.5 tw-rounded-md tw-border tw-border-solid tw-px-2.5 tw-py-2 tw-transition-colors",
        option.isCorrect ? "tw-border-success/60 tw-bg-success/[0.07]" : "tw-border-line tw-bg-surface-raised",
      )}
    >
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-inline-flex tw-h-[26px] tw-w-[26px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-muted tw-text-xs tw-font-medium tw-text-content">
          {letter}
        </span>
        {/* aria-pressed: a toggle, and the label alone never said which state it was in. */}
        <button
          type="button"
          aria-pressed={option.isCorrect}
          aria-label={`Option ${letter} is correct`}
          title={option.isCorrect ? "Marked correct" : "Mark correct"}
          onClick={onToggleCorrect}
          className={cn(
            "tw-inline-flex tw-h-6 tw-w-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center",
            "tw-rounded-[0.3rem] tw-border-[1.5px] tw-border-solid tw-transition-colors tw-focus-ring",
            option.isCorrect
              ? "tw-border-success tw-bg-success tw-text-on-status"
              : "tw-border-line tw-bg-surface-raised tw-text-transparent hover:tw-border-success hover:tw-text-success",
          )}
        >
          <Check size={14} aria-hidden="true" />
        </button>
        {/* The row draws the border, so the control inside drops its own. */}
        <Input
          aria-label={`Option ${letter}`}
          className="tw-flex-1 tw-border-0 !tw-bg-transparent tw-px-[0.4rem] focus:!tw-shadow-none"
          value={option.text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder={`Option ${letter}`}
          disabled={fixed}
        />
        {onRemove && (
          <IconButton label={`Remove option ${letter}`} icon={X} size="xs" variant="ghost-destructive" onClick={onRemove} />
        )}
      </div>
      {children && <div className="tw-pl-[3.75rem]">{children}</div>}
    </div>
  );
}

/**
 * Step 4 — what counts as correct, and only what this type needs: options with
 * the correct ones ticked, the typed answers accepted, or the number and how
 * close is close enough.
 */
export default function AnswerStep({ form, setForm, set, errors }) {
  const kind = answerKind(form.questionType);
  const trueFalse = form.questionType === "true_false";
  const multi = form.questionType === "multi_select";

  if (kind === "options") {
    return (
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Options</AppCardTitle>
          <AppCardDescription>
            {trueFalse
              ? "Tick whichever is correct."
              : multi
                ? "Write the options, then tick every one that is correct. Students must pick all of them."
                : "Write the options, then tick the one that is correct."}
          </AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-2">
          {form.options.map((opt, i) => (
            <OptionRow
              key={opt._id || i}
              letter={letterOf(i)}
              option={opt}
              fixed={trueFalse}
              onToggleCorrect={() => setForm((f) => setOption(f, i, { isCorrect: !opt.isCorrect }))}
              onTextChange={(text) => setForm((f) => setOption(f, i, { text }))}
              onRemove={!trueFalse && form.options.length > 2 ? () => setForm((f) => removeOption(f, i)) : null}
            >
              {form.questionType === "image_mcq" && (
                <OptionImageControl
                  letter={letterOf(i)}
                  value={opt.imageUrl || ""}
                  onChange={(url) => setForm((f) => setOption(f, i, { imageUrl: url }))}
                />
              )}
            </OptionRow>
          ))}
          {errors.options && <FieldError>{errors.options}</FieldError>}
          {!trueFalse && (
            <div>
              <AppButton type="button" variant="ghost" size="sm" onClick={() => setForm(addOption)}>
                <Plus size={16} aria-hidden="true" /> Add option
              </AppButton>
            </div>
          )}
        </AppCardContent>
      </AppCard>
    );
  }

  if (kind === "text") {
    return (
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Accepted answers</AppCardTitle>
          <AppCardDescription>
            Every spelling that counts as correct. Capitals do not matter; anything else must match exactly.
          </AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-2">
          {form.acceptedAnswers.map((a, i) => (
            <div key={`a-${i}`} className="tw-flex tw-items-center tw-gap-1.5">
              <Input
                aria-label={`Accepted answer ${i + 1}`}
                value={a}
                onChange={(e) => setForm((f) => setAnswer(f, i, e.target.value))}
                placeholder={i === 0 ? "e.g. photosynthesis" : `Another accepted answer`}
                invalid={!!errors.acceptedAnswers && i === 0}
              />
              {form.acceptedAnswers.length > 1 && (
                <IconButton
                  label={`Remove accepted answer ${i + 1}`}
                  icon={X}
                  size="xs"
                  variant="ghost-destructive"
                  onClick={() => setForm((f) => removeAnswer(f, i))}
                />
              )}
            </div>
          ))}
          {errors.acceptedAnswers && <FieldError>{errors.acceptedAnswers}</FieldError>}
          <div>
            <AppButton type="button" variant="ghost" size="sm" onClick={() => setForm(addAnswer)}>
              <Plus size={16} aria-hidden="true" /> Add another spelling
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>
    );
  }

  if (kind === "numeric") {
    return (
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>The number</AppCardTitle>
          <AppCardDescription>Students type a number. It is marked correct within the tolerance you set.</AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-grid tw-gap-4 min-[768px]:tw-grid-cols-2">
          <FormField label="Correct answer" required error={errors.numericAnswer}>
            <Input
              type="number"
              step="any"
              required={false}
              value={form.numericAnswer}
              onChange={(e) => set("numericAnswer", e.target.value)}
              placeholder="e.g. 42 or 3.14"
            />
          </FormField>
          <FormField label="Tolerance (±)" hint="0 means an exact match; 0.01 also accepts anything within 0.01.">
            <Input
              type="number"
              min={0}
              step="any"
              value={form.numericTolerance}
              onChange={(e) => set("numericTolerance", Math.max(0, Number.parseFloat(e.target.value) || 0))}
            />
          </FormField>
        </AppCardContent>
      </AppCard>
    );
  }

  return null;
}
